import React, {useEffect, useRef, useState} from 'react';
import FriendlyCaptcha from '@core/scripts/components/FriendlyCaptcha.jsx';
import meta from '@core/scripts/helper/meta';
import {FormProvider, useForm} from 'react-hook-form';
import {emailValidation, passwordValidation} from '@core/scripts/helper/form-input-validation/inputFieldValidations';
import {doSendMetric} from '@core/scripts/helper/send-metric-helper.js';
import CheckboxWithToolTipField from '@checkout/components/common/CheckboxWithToolTipField.jsx';
import {PermanentLoginConfirmationTipText} from '@checkout/components/login/PermanentLoginToolTipText.jsx';
import Device from '@core/scripts/components/device.js';
import ControlledInputField from '@checkout/components/common/forms/fields/ControlledInputField.jsx';
import {Button, Link} from '@bonprix/design-system';
import {
    trackClickStayLoggedInCheckBoxStatus, trackClickStayLoggedInInfo,
    trackViewStayLoggedInCheckBox
} from "@checkout/components/helper/TrinityTrackingHelper.js";
import {GlobalLogoutTracking} from "@account/shared-components/tracking/GlobalLogoutTracking.ts";
import {submitLoginForm} from '@core/scripts/helper/submit-login-form';

export default function LoginForm({
    texts,
    username,
    loginFormAction,
    siteKeyLogin,
    isCaptchaEnabled,
    isCaptchaV2Enabled,
    captchaLang,
    onForgotPassword,
}) {
    const [loading, setLoading] = useState(false);
    const [captchaCompletedSuccessfully, setCaptchaCompletedSuccessfully] = useState(false);

    // this is necessary for correct validation of the form in case of already used email error from presignup
    // because the email address is set by the message broker in login.jsx and login form is not aware of this value until first submit
    const [alreadyUsedEmail, setAlreadyUsedEmail] = useState('');
    const loginRef = useRef();
    const captchaStartRef = useRef(0);
    //a ref, not the loading state: two submits dispatched before the next render would both read a stale `loading`
    const submittingRef = useRef(false);

    const defaultValues = {
        username: username || '',
        password: '',
        permanentLoginConsent: false,
    };

    const {
        control,
        register,
        handleSubmit,
        getValues,
        setValue,
        setError,
        formState: {errors, touchedFields, isDirty, isValid, isSubmitSuccessful},
        trigger,
    } = useForm({
        mode: 'onTouched',
        defaultValues: defaultValues,
    });

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('isGlobalLogoutSuccess') === 'true') {
            GlobalLogoutTracking.trackLayerViewSuccessPage()
        }
    }, []);

    //navigating away while the post is still running leaves the form in its loading state, and coming back restores
    //the page from the bfcache exactly as it was - without this the customer would find a button spinning for good.
    useEffect(() => {
        const resetOnBfCacheRestore = (event) => {
            if (event.persisted) {
                submittingRef.current = false;
                setLoading(false);
            }
        };
        window.addEventListener('pageshow', resetOnBfCacheRestore);
        return () => window.removeEventListener('pageshow', resetOnBfCacheRestore);
    }, []);

    const handleCaptchaStart = () => {
        captchaStartRef.current = Date.now();
    };

    const handleVerifyCallback = (solution) => {
        if (solution) {
            doSendMetric('captcha.login.duration', Date.now() - captchaStartRef.current);
        }
        setCaptchaCompletedSuccessfully(Boolean(solution));
    };

    const handleErrorCallback = (error) => {
        setCaptchaCompletedSuccessfully(true);
        doSendMetric('captcha.login.error', 1.0);
    };

    const formErrorsObjectIsNotEmpty = () => {
        return Object.entries(errors).length > 0;
    };

    const onSubmit = async () => {
        if (submittingRef.current) {
            return;
        }
        submittingRef.current = true;
        setLoading(true);

        const loginFormElement = loginRef.current;
        loginFormElement.action = loginFormAction + encodeURIComponent(window.location.hash);

        createHiddenCsrfField(meta.value('_csrf'));
        createHiddenField(loginFormElement, 'permanentLoginConsent', String(getValues('permanentLoginConsent') === true));

        if (!(await submitLoginForm(loginFormElement))) {
            //the browser did not go anywhere - either the request never left, or the navigation after it failed.
            //Give the form back either way, otherwise the customer is stuck in front of a spinning button.
            submittingRef.current = false;
            setLoading(false);
        }
    };

    const createHiddenField = (formElement, name, value) => {
        //a retry must not leave the previous attempt's field behind: the servlet reads the FIRST value of a
        //duplicate parameter, so a customer who toggles the checkbox in between would post the stale one
        formElement.querySelector(`input[type="hidden"][name="${name}"]`)?.remove();

        const inputField = document.createElement('input');
        inputField.setAttribute('type', 'hidden');
        inputField.setAttribute('name', name);
        inputField.setAttribute('value', value);
        formElement.appendChild(inputField);
    };

    const createHiddenCsrfField = (token) => {
        const loginFormElement = loginRef.current;
        createHiddenField(loginFormElement, '_csrf', token);
    };

    const trinityPageName = 'login';

    const trackStayLoginCheckboxChange = (isActivated) => {
        trackClickStayLoggedInCheckBoxStatus(trinityPageName, isActivated);
    };

    const trackStayLoginTooltipOpened = (changedToOpen) => {
        if (changedToOpen) {
            trackClickStayLoggedInInfo(trinityPageName);
        }
    };

    const trackStayLoginCheckboxIsDisplayed = () => {
        const handleTabChange = (event) => {
            const {activeTab} = event.detail;
            if (activeTab === trinityPageName) {
                trackViewStayLoggedInCheckBox(trinityPageName);
            }
        };

        window.addEventListener('viewTab', handleTabChange);

        return () => {
            window.removeEventListener('viewTab', handleTabChange);
        };
    };

    return (
        <FormProvider
            register={register}
            formState={{errors, touchedFields, isDirty, isValid, isSubmitSuccessful}}
            getValues={getValues}
            setValue={setValue}
            setError={setError}
            trigger={trigger}
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="flex flex-col gap-2.5 mb-12 mt-6 m:mt-8 mx-auto items-start m:w-8/12 xl:w-3/5"
                ref={loginRef}
            >
                <ControlledInputField
                    type="email"
                    name="username"
                    label={texts['cot09_email_address']}
                    validation={emailValidation(texts, alreadyUsedEmail)}
                    control={control}
                />
                <ControlledInputField
                    type="password"
                    name="password"
                    label={texts['cot09_password']}
                    validation={passwordValidation(texts)}
                    control={control}
                />
                {!Device.isApp() && (
                    <CheckboxWithToolTipField
                        control={control}
                        name="permanentLoginConsent"
                        checkboxLabel={texts.cus__permanentLogin_checkbox_label}
                        toolTipText={<PermanentLoginConfirmationTipText texts={texts} pageName={trinityPageName} />}
                        viewTrackingCallback={trackStayLoginCheckboxIsDisplayed}
                        changeCheckboxTrackingCallback={trackStayLoginCheckboxChange}
                        tooltipTrackingCallback={trackStayLoginTooltipOpened}
                    />
                )}
                {isCaptchaEnabled && (
                    //In this case no "resetSwitch" is needed because the login page with errors is always a complete reload
                    <FriendlyCaptcha
                        v2={isCaptchaV2Enabled}
                        lang={captchaLang}
                        siteKey={siteKeyLogin}
                        verifyCallback={handleVerifyCallback}
                        errorCallback={handleErrorCallback}
                        startMode={'auto'}
                        onCaptchaStart={handleCaptchaStart}
                    />
                )}
                <div className="mb-12">
                    <Link component="button" children={texts.cot20_customer_lost_password_layer_title} onClick={onForgotPassword} />
                </div>
                <Button
                    type="submit"
                    onClick={() => setAlreadyUsedEmail(document.querySelector('[name="username"]')?.value)}
                    variant="primary"
                    loading={loading}
                    disabled={formErrorsObjectIsNotEmpty() || (isCaptchaEnabled && !captchaCompletedSuccessfully)}
                >
                    {texts['checkout_login_submit_button']}
                </Button>
            </form>
        </FormProvider>
    );
}
